import React, { useState } from 'react';
import { Share2, Sparkles, MessageCircle, ArrowRight, Download, Check } from 'lucide-react';
import { emitSnapshotCtaTapped } from '../../utils/snapshotService';

/**
 * KundaliSnapshotCard (D4)
 * Single card in the D9a token system.
 * Used on /snapshot screen and as the today-first hero on the Home dashboard.
 *
 * Invariant: Never mentions "599" or "free trial" text — copy is strictly flag-driven.
 */
const KundaliSnapshotCard = ({
  snapshot,
  loading = false,
  userName = 'Seeker',
  lang = 'hinglish',
  onAskQuestion,
  onOpenShare,
  testId = 'kundali-snapshot-card',
  trialAvailable = false,
}) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  if (loading) {
    return (
      <div
        className="w-full rounded-2xl border border-[var(--card-border)] bg-[var(--card-glass)] backdrop-blur-md p-5 text-white animate-pulse space-y-4"
        data-testid={`${testId}-skeleton`}
      >
        <div className="flex items-center justify-between">
          <div className="h-5 w-32 bg-white/10 rounded-md" />
          <div className="h-4 w-24 bg-white/10 rounded-full" />
        </div>
        <div className="h-12 w-full bg-white/5 rounded-xl border border-white/5" />
        <div className="flex gap-2">
          <div className="h-7 w-1/2 bg-emerald-950/40 rounded-full" />
          <div className="h-7 w-1/2 bg-rose-950/40 rounded-full" />
        </div>
        <div className="h-6 w-full bg-white/5 rounded-lg" />
        <div className="space-y-2 pt-1">
          <div className="h-14 bg-white/5 rounded-xl" />
          <div className="h-14 bg-white/5 rounded-xl" />
          <div className="h-14 bg-white/5 rounded-xl" />
        </div>
        <div className="h-10 w-full bg-amber-500/20 rounded-xl" />
      </div>
    );
  }

  if (!snapshot) return null;

  const { chart, facts = [], today = {}, mood_line = {}, next = {}, share_card_url, intent = 'general' } = snapshot;
  const freeQuestionAvailable = !!next?.free_question_available;
  const chips = Array.isArray(next?.chips) ? next.chips : [];

  // Localized text resolution
  const isHi = lang === 'hi';
  const isEn = lang === 'en';

  const moodText = mood_line?.[lang] || mood_line?.text || (isHi ? mood_line?.hi : (isEn ? mood_line?.en : mood_line?.hinglish)) || '';

  // Lagna, Moon nakshatra, current MD/AD
  const lagnaSign = chart?.lagna?.sign || '';
  const lagnaDeg = chart?.lagna?.degree != null ? `${chart.lagna.degree}°` : '';
  const moonNak = chart?.moon?.nakshatra || '';
  const moonSign = chart?.moon?.sign || '';
  const mdPlanet = chart?.mahadasha?.planet || '';
  const adPlanet = chart?.antardasha?.planet || '';

  // Primary CTA text logic
  // 1. If free_question_available: "Ek sawaal poochho — free"
  // 2. Else if trialAvailable (when SNAPSHOT_REPLACES_TRIAL is off in backend): "Pehla session shuru karein"
  // 3. Else (normal session): "ChitraGupt se poochho"
  const getPrimaryCtaText = () => {
    if (freeQuestionAvailable) {
      if (isHi) return 'एक सवाल पूछें — मुफ़्त';
      if (isEn) return 'Ask one question — free';
      return 'Ek sawaal poochho — free';
    }
    if (trialAvailable) {
      if (isHi) return 'पहला सत्र शुरू करें';
      if (isEn) return 'Start first session';
      return 'Pehla session shuru karein';
    }
    if (isHi) return 'चित्रगुप्त से पूछें';
    if (isEn) return 'Ask ChitraGupt';
    return 'ChitraGupt se poochho';
  };

  const handleChipClick = (chipText) => {
    emitSnapshotCtaTapped({
      ctaType: 'chip',
      chipText,
      intent,
      freeQuestionAvailable,
    });
    if (onAskQuestion) {
      onAskQuestion(chipText, intent, { freeQuestion: freeQuestionAvailable });
    }
  };

  const handlePrimaryClick = () => {
    const defaultQuestion = chips[0] || (isHi ? 'मेरी कुंडली का विश्लेषण करें' : 'Meri kundali ka vishleshan karein');
    emitSnapshotCtaTapped({
      ctaType: 'primary',
      chipText: defaultQuestion,
      intent,
      freeQuestionAvailable,
    });
    if (onAskQuestion) {
      onAskQuestion(defaultQuestion, intent, { freeQuestion: freeQuestionAvailable });
    }
  };

  const handleShareClick = async () => {
    emitSnapshotCtaTapped({
      ctaType: 'share',
      intent,
      freeQuestionAvailable,
    });

    if (onOpenShare) {
      onOpenShare(share_card_url);
      return;
    }

    if (!share_card_url) return;

    if (navigator.share) {
      try {
        setIsSharing(true);
        await navigator.share({
          title: isHi ? 'मेरी कुंडली स्नैपशॉट' : 'Meri Kundali Snapshot',
          text: isHi
            ? 'ज्योतिषपुराण पर मेरी कुंडली स्नैपशॉट और आज का शुभ समय देखें:'
            : 'JyotishPuraan par meri Kundali Snapshot aur aaj ka shubh samay dekhein:',
          url: share_card_url,
        });
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('[SNAPSHOT-SHARE] Native share failed:', err);
        }
      } finally {
        setIsSharing(false);
      }
    }

    // Fallback: copy link or open PNG
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(share_card_url);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2500);
      } else {
        window.open(share_card_url, '_blank');
      }
    } catch {
      window.open(share_card_url, '_blank');
    }
  };

  return (
    <div
      className="w-full rounded-2xl border border-[var(--card-border)] bg-[var(--card-glass)] backdrop-blur-md p-5 text-white shadow-xl space-y-4"
      data-testid={testId}
    >
      {/* Header: Name and Snapshot Pill */}
      <div className="flex items-center justify-between border-b border-white/10 pb-3">
        <div>
          <span className="text-[10px] uppercase tracking-wider text-[var(--gold-primary)] font-semibold block">
            {isHi ? 'कुंडली स्नैपशॉट' : 'Kundali Snapshot'}
          </span>
          <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-lg font-bold text-white tracking-wide">
            {userName}
          </h2>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-300 font-medium">
          <Sparkles className="w-3 h-3 text-amber-400" />
          <span>{isHi ? 'आज का गोचर' : 'Aaj ka Gochar'}</span>
        </div>
      </div>

      {/* "Aaj ka din" line: mood_line */}
      {moodText && (
        <div
          className="border-l-2 border-[var(--gold-primary)] pl-3 py-1.5 bg-amber-500/[0.04] rounded-r-xl"
          data-testid="snapshot-mood-line"
        >
          <span className="text-[11px] font-semibold text-[var(--gold-primary)] uppercase tracking-wider block">
            {isHi ? 'आज का दिन' : 'Aaj ka din'}
          </span>
          <p className="text-xs text-white/90 leading-relaxed mt-0.5">{moodText}</p>
        </div>
      )}

      {/* Good Window & Avoid Window as two Choghadiya pills */}
      {(today?.good_window || today?.avoid_window) && (
        <div className="grid grid-cols-2 gap-2 pt-0.5" data-testid="snapshot-windows-strip">
          {today?.good_window && (
            <div
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-950/60 border border-emerald-500/40 text-emerald-300 text-xs font-medium"
              data-testid="snapshot-good-window"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0 animate-pulse" />
              <div className="truncate">
                <span className="text-[10px] text-emerald-400/80 block uppercase font-bold">
                  {isHi ? 'शुभ समय' : 'Shubh'}
                </span>
                <span className="font-semibold text-emerald-100">{today.good_window}</span>
              </div>
            </div>
          )}

          {today?.avoid_window && (
            <div
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-950/60 border border-rose-500/40 text-rose-300 text-xs font-medium"
              data-testid="snapshot-avoid-window"
            >
              <span className="w-2 h-2 rounded-full bg-rose-400 shrink-0" />
              <div className="truncate">
                <span className="text-[10px] text-rose-400/80 block uppercase font-bold">
                  {isHi ? 'वर्ज्य समय' : 'Varjya'}
                </span>
                <span className="font-semibold text-rose-100">{today.avoid_window}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lagna + Moon nakshatra + current MD/AD in one compact line */}
      <div
        className="px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-[11px] text-white/80 flex flex-wrap items-center justify-between gap-1.5"
        data-testid="snapshot-chart-summary"
      >
        <span className="font-medium text-amber-200">
          <strong className="text-white/60 font-normal">{isHi ? 'लग्न:' : 'Lagna:'}</strong> {lagnaSign}{' '}
          {lagnaDeg && <span className="text-[10px] text-white/50">({lagnaDeg})</span>}
        </span>
        <span className="text-white/20">·</span>
        <span className="font-medium text-amber-200">
          <strong className="text-white/60 font-normal">{isHi ? 'नक्षत्र:' : 'Nakshatra:'}</strong> {moonNak}{' '}
          {moonSign && <span className="text-[10px] text-white/50">({moonSign})</span>}
        </span>
        <span className="text-white/20">·</span>
        <span className="font-medium text-amber-200">
          <strong className="text-white/60 font-normal">{isHi ? 'दशा:' : 'Dasha:'}</strong> {mdPlanet}/{adPlanet}
        </span>
      </div>

      {/* The three facts as three rows */}
      {facts.length > 0 && (
        <div className="space-y-2 pt-1" data-testid="snapshot-facts-container">
          {facts.slice(0, 3).map((fact, idx) => {
            const title =
              isHi ? fact.title_hi : (isEn ? fact.title_en : (fact.title_hinglish || fact.title_hi || fact.title_en));
            const line =
              isHi ? fact.line_hi : (isEn ? fact.line_en : (fact.line_hinglish || fact.line_hi || fact.line_en));

            return (
              <div
                key={fact.key || idx}
                className="p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-amber-500/20 transition-colors space-y-0.5"
                data-testid={`snapshot-fact-row-${idx}`}
              >
                <div className="text-xs font-semibold text-[var(--gold-primary)] flex items-center gap-1.5">
                  <span className="text-[10px] text-amber-400">✦</span>
                  <span>{title}</span>
                </div>
                <p className="text-xs text-white/70 leading-relaxed">{line}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Chips: 3 suggested questions */}
      {chips.length > 0 && (
        <div className="space-y-1.5 pt-1" data-testid="snapshot-chips-container">
          <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold px-0.5">
            {isHi ? 'एक टैप में पूछें:' : 'Ek tap mein poochhein:'}
          </div>
          <div className="flex flex-col gap-1.5">
            {chips.slice(0, 3).map((chip, idx) => (
              <button
                key={idx}
                onClick={() => handleChipClick(chip)}
                className="text-left text-xs p-2.5 rounded-xl bg-amber-500/[0.08] hover:bg-amber-500/15 border border-amber-500/20 hover:border-amber-500/40 text-amber-100 flex items-center justify-between transition-colors group cursor-pointer"
                data-testid={`snapshot-chip-${idx}`}
              >
                <div className="flex items-center gap-2 pr-2">
                  <MessageCircle className="w-3.5 h-3.5 text-amber-400/80 shrink-0" />
                  <span className="leading-snug">{chip}</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-amber-400 opacity-60 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons: ONE Primary + Secondary Share */}
      <div className="space-y-2 pt-2 border-t border-white/10">
        <button
          onClick={handlePrimaryClick}
          className="w-full py-3 px-4 rounded-xl font-bold text-sm bg-gradient-to-r from-[var(--gold-primary)] to-amber-500 text-[var(--text-on-gold)] shadow-lg shadow-amber-900/30 hover:brightness-110 active:scale-[0.99] transition-all flex items-center justify-center gap-2"
          data-testid="snapshot-primary-cta"
        >
          <Sparkles className="w-4 h-4 text-black" />
          <span>{getPrimaryCtaText()}</span>
        </button>

        {share_card_url && (
          <button
            onClick={handleShareClick}
            disabled={isSharing}
            className="w-full py-2.5 px-3 rounded-xl font-medium text-xs bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 hover:text-white transition-colors flex items-center justify-center gap-2"
            data-testid="snapshot-share-btn"
          >
            {copiedLink ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-300">{isHi ? 'लिंक कॉपी हो गया!' : 'Link copy ho gaya!'}</span>
              </>
            ) : (
              <>
                <Share2 className="w-3.5 h-3.5 text-amber-400" />
                <span>{isHi ? 'कार्ड शेयर करें' : 'Card share karein'}</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default KundaliSnapshotCard;
